import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "stores/RootStore";
import ServiceStore from "stores/ServiceStore";
import { Account } from "types/common/ServiceStoreTypes";
import { AccountFormData } from "../../../../types";

export interface ValidationErrors {
  name?: string;
  accountType?: string;
  industry?: string;
  status?: string;
  website?: string;
  phone?: string;
  email?: string;
  annualRevenue?: string;
  employeeCount?: string;
  [key: string]: string | undefined;
}

/**
 * Store for managing account form state and operations
 * Handles account creation and editing with form validation
 */
export class AccountFormStore {
  rootStore: RootStore;

  formData: AccountFormData = {
    name: "",
    accountType: "Customer",
    industry: "",
    isActive: true,
  };
  errors: ValidationErrors = {};
  isDirty = false;
  isEditMode = false;
  accountId?: number;

  // Constructor
  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {
      rootStore: false,
    });
  }

  // Actions
  /**
   * Set a specific form field value
   * @param field - The form field to update
   * @param value - The new value for the field
   */
  setField<K extends keyof AccountFormData>(
    field: K,
    value: AccountFormData[K]
  ) {
    console.log(`Setting field ${field} to`, value);
    console.log("Current form data:", this.formData);
    this.formData[field] = value;
    this.isDirty = true;

    // Clear field-specific error when user starts typing
    if (this.errors[field]) {
      delete this.errors[field];
    }
  }

  /**
   * Update multiple form fields at once
   * @param data - Partial form data to merge with current form data
   */
  setFormData(data: Partial<AccountFormData>) {
    this.formData = { ...this.formData, ...data };
    this.isDirty = true;
  }

  /**
   * Reset form to initial state
   */
  resetForm() {
    this.formData = {
      name: "",
      accountType: "Customer",
      industry: "",
      isActive: true,
    };
    this.errors = {};
    this.isDirty = false;
  }

  /**
   * Copy billing address to shipping address
   */
  copyBillingToShipping() {
    runInAction(() => {
      this.formData.shippingAddress = this.formData.billingAddress;
      this.isDirty = true;
    });
  }

  // Async actions
  /**
   * Load existing account data for editing
   * @param accountId - The ID of the account to load
   */
  async loadAccount(accountId: number) {
    this.isEditMode = true;
    this.accountId = accountId;

    try {
      const account = (await this.customersStore.get(
        accountId
      )) as unknown as Account;

      // Map account data to form data
      runInAction(() => {
        this.formData = {
          name: account.name,
          accountType: account.accountType,
          industry: account.industry,
          isActive: account.isActive,
          parentAccountId: account.parentAccountId,
          website: account.website,
          description: account.description,
          annualRevenue: account.annualRevenue,
          employeeCount: account.employeeCount,
          phone: account.phone,
          email: account.email,
          billingAddress: account.billingAddress,
          shippingAddress: account.shippingAddress,
        };
        this.isDirty = false;
      });
    } catch (error) {
      console.error("Failed to load account:", error);
      throw error;
    }
  }

  /**
   * Save the account (create new or update existing)
   * @returns The saved account or null if validation fails
   */
  async saveAccount(): Promise<Account | null> {
    // if (!this.validateForm()) {
    //   return null;
    // }

    try {
      let savedAccount: Account;

      if (this.isEditMode && this.accountId) {
        // Update existing account
        savedAccount = (await this.customersStore.patch(
          this.accountId,
          this.formData
        )) as unknown as Account;
      } else {
        // Create new account
        savedAccount = (await this.customersStore.create({
          ...this.formData,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        })) as unknown as Account;
      }

      runInAction(() => {
        this.isDirty = false;
      });
      return savedAccount;
    } catch (error) {
      console.error("Failed to save account:", error);
      throw error;
    }
  }

  // Computed values
  /**
   * Access to the accounts service store
   */
  get customersStore() {
    return this.rootStore.customersStore as ServiceStore;
  }

  /**
   * Loading state from accounts store
   */
  get loading(): boolean {
    return this.customersStore.isServicePending;
  }

  /**
   * Saving state from accounts store
   */
  get saving(): boolean {
    return this.customersStore.isServicePending;
  }

  /**
   * Whether the form is currently valid
   */
  get isValid(): boolean {
    return (
      Object.keys(this.errors).length === 0 && this.formData.name.trim() !== ""
    );
  }

  /**
   * Whether the form can be saved (valid, dirty, not currently saving)
   */
  get canSave(): boolean {
    return this.isValid && this.isDirty && !this.loading;
  }

  /**
   * Whether all required fields are filled
   */
  get hasRequiredFields(): boolean {
    return !!this.formData.name.trim();
  }

  // Private methods
  /**
   * Validate the current form data
   * @returns True if form is valid, false otherwise
   */
  private validateForm(): boolean {
    const errors: ValidationErrors = {};

    // Required field - only name is required per API schema
    if (!this.formData.name.trim()) {
      errors.name = "Account name is required";
    }

    // Website validation
    if (this.formData.website && !this.isValidUrl(this.formData.website)) {
      errors.website = "Please enter a valid website URL";
    }

    // Email validation
    if (this.formData.email && !this.isValidEmail(this.formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    // Phone validation
    if (this.formData.phone && this.formData.phone.length > 50) {
      errors.phone = "Phone number cannot exceed 50 characters";
    }

    // Numeric field validations
    if (
      this.formData.annualRevenue !== undefined &&
      this.formData.annualRevenue < 0
    ) {
      errors.annualRevenue = "Annual revenue must be positive";
    }

    if (
      this.formData.employeeCount !== undefined &&
      this.formData.employeeCount < 0
    ) {
      errors.employeeCount = "Employee count must be positive";
    }

    this.errors = errors;
    return Object.keys(errors).length === 0;
  }

  /**
   * Validate URL format
   * @param url - URL to validate
   * @returns True if URL is valid
   */
  private isValidUrl(url: string): boolean {
    try {
      // Add protocol if missing
      const urlWithProtocol = url.startsWith("http") ? url : `https://${url}`;
      new URL(urlWithProtocol);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Validate email format
   * @param email - Email to validate
   * @returns True if email is valid
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}

export default AccountFormStore;
